import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { AlertTriangle, Calendar, Check, Clock, ClipboardList, Copy, Mail, MapPin, MessageCircle, Package, Pencil, Store, Truck, User } from "lucide-react";
import dayjs from "dayjs";
import { DatePicker, DatesProvider } from "@mantine/dates";
import "dayjs/locale/ar";
import DropoffLocationField from "../../location/DropoffLocationField";
import {
  createManualTrackHubShipment,
  fetchTrackHubBranches,
  fetchTrackHubCarriers,
  quoteTrackHubShipment,
} from "../../../services/trackHubApi";
import {
  formatTrackHubApiError,
  quoteCarrierRestrictionMessage,
} from "../../../utils/formatTrackHubError";
import {
  formatKsaE164,
  isValidKsaPhone,
  normalizeKsaMobile,
} from "../../../utils/validators";
import OrderSelectionField from "./OrderSelectionField";
import PriceDisplay from "../../PriceDisplay";
import SARIcon from "../../SARIcon";
import PickupBranchMap, { branchCoords } from "./PickupBranchMap";
import "../../location/dropoff-location-field.css";
import "./create-manual-shipment-page.css";

function MoneyInline({ value, iconSize = 12, symbolColor = "" }) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  return (
    <span className="cmsw__money-inline" dir="ltr">
      <PriceDisplay value={amount} iconSize={iconSize} color={symbolColor} />
    </span>
  );
}

function isRetryableShipmentCreateError(error) {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const reason = data?.reason;
  if (status === 502 || status === 504 || reason === "timeout") return true;
  const code = data?.error_code;
  if (code === "TRACKHUB_UNAVAILABLE" || code === "QUOTE_FAILED") return true;
  const message = String(data?.message || error?.message || "").toLowerCase();
  return message.includes("timeout") || message.includes("temporarily");
}

const AR_WEEKDAY_LABELS = ["أحد", "إثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"];
const arabicWeekdayFormat = (date) => AR_WEEKDAY_LABELS[dayjs(date).day()];

function toPickerYmd(value) {
  if (!value) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const d = dayjs(value);
  return d.isValid() ? d.format("YYYY-MM-DD") : null;
}

const SCHEDULE_MAX_DAYS_AHEAD = 90;
/** Days 0..(n-1) use the horizontal chips; the “More” calendar starts at offset n. */
const QUICK_PICK_DAY_COUNT = 7;
const MAX_SHIPMENT_WEIGHT_KG = 30;
const QUOTE_GUIDE_MAX_KM = 30;
const QUOTE_GUIDE_MAX_KG = 30;
const FOOD_SERVICE_SLA_MIN = 90;
const DEFAULT_DELIVERY_LEG_MIN = 27;

const TRACKHUB_PACKAGE_SIZE = {
  bag: "BAG",
  box: "BOX",
  large: "LARGE_BOX",
};

const SIZES = ["bag", "box", "large"];
const SLOT_GROUPS = [
  ["morning", 0, 12],
  ["afternoon", 12, 17],
  ["evening", 17, 21],
  ["night", 21, 24],
];
const LOCATION_CHIPS = [
  { id: "maps", labelKey: "sampleMaps", value: "https://maps.google.com/?q=24.7136,46.6753" },
  { id: "plus", labelKey: "samplePlus", value: "8G7M+2R Riyadh" },
  { id: "coords", labelKey: "sampleCoords", value: "24.7136, 46.6753" },
  { id: "national", labelKey: "sampleNational", value: "RRRD2929" },
  { id: "address", labelKey: "sampleAddress", value: "Olaya Street, Riyadh" },
];
const QUOTE_HOLD_MS = 5 * 60 * 1000;

function roundMoney(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

function parseCoord(value) {
  const s = String(value ?? "").trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function carrierFeeOf(quote) {
  const n = Number(quote?.carrierAmount != null ? quote.carrierAmount : quote?.amount);
  return Number.isFinite(n) ? n : null;
}

function money(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toFixed(2);
}

function MoneyAmount({ value, iconSize = 12, className = "" }) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return <span className={className}>—</span>;
  return (
    <b className={className}>
      <PriceDisplay value={amount} iconSize={iconSize} />
    </b>
  );
}

function phoneDigits(value) {
  let d = String(value ?? "").replace(/\D/g, "");
  if (d.startsWith("966")) d = d.slice(3);
  if (d.startsWith("0")) d = d.slice(1);
  return d.slice(0, 9);
}

function fmtPhone(digits) {
  return [digits.slice(0, 2), digits.slice(2, 5), digits.slice(5, 9)].filter(Boolean).join(" ");
}

function displayPhone(value) {
  const digits = phoneDigits(value);
  return digits ? `+966 ${fmtPhone(digits)}` : "";
}

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  const word = parts[0] || "?";
  return `${word[0] || "?"}${word[1] || ""}`.toUpperCase();
}

function pad(n) {
  return String(n).padStart(2, "0");
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function ymd(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function parseYmd(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(date.getTime())) return null;
  return startOfDay(date);
}

function dayDate(offset) {
  const d = startOfDay(new Date());
  d.setDate(d.getDate() + offset);
  return d;
}

function selectedPickupDate(schedDate) {
  return parseYmd(schedDate) || dayDate(0);
}

function daysFromToday(date) {
  return Math.round((startOfDay(date).getTime() - dayDate(0).getTime()) / 86400000);
}

function slotDateOn(date, slot) {
  const d = startOfDay(date);
  const [h, m] = String(slot || "").split(":").map(Number);
  d.setHours(h || 0, m || 0, 0, 0);
  return d;
}

function slotsForDate(date) {
  const min = Date.now() + 45 * 60 * 1000;
  const out = [];
  for (let t = 8; t <= 23.5; t += 0.5) {
    const s = `${pad(Math.floor(t))}:${t % 1 ? "30" : "00"}`;
    out.push({ s, t, ok: slotDateOn(date, s).getTime() >= min });
  }
  return out;
}

function packagePayload(form) {
  const type = String(form.packageType || "").trim();
  const rawWeight = String(form.weightKg ?? "").trim();
  if (!rawWeight) {
    return type ? { type } : null;
  }
  const weight = Number(rawWeight);
  if (!Number.isFinite(weight) || weight <= 0 || weight > MAX_SHIPMENT_WEIGHT_KG) {
    return null;
  }
  return type ? { weightKg: weight, type } : { weightKg: weight };
}

function roadKm(aLat, aLng, bLat, bLng) {
  if ([aLat, aLng, bLat, bLng].some((n) => !Number.isFinite(n))) return null;
  const R = 6371;
  const r = Math.PI / 180;
  const dLat = (bLat - aLat) * r;
  const dLng = (bLng - aLng) * r;
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(aLat * r) * Math.cos(bLat * r) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h)) * 1.3;
}

function tagKind(tag) {
  const s = String(tag || "").toLowerCase();
  if (s.includes("recommend")) return "recommended";
  if (s.includes("cheap")) return "cheapest";
  if (s.includes("fast")) return "fastest";
  return null;
}

function assignBadges(rows) {
  const badges = {};
  const ready = rows.filter((row) => row.status === "ok" && row.quote);
  ready.forEach((row) => {
    const set = new Set();
    (row.quote.tags || []).forEach((tag) => {
      const kind = tagKind(tag);
      if (kind) set.add(kind);
    });
    badges[row.id] = set;
  });
  const has = (kind) => Object.values(badges).some((set) => set.has(kind));
  if (!has("cheapest") && ready.length) {
    const cheapest = [...ready].sort((a, b) => carrierFeeOf(a.quote) - carrierFeeOf(b.quote))[0];
    badges[cheapest.id].add("cheapest");
  }
  if (!has("fastest")) {
    const withEta = ready.filter((row) => Number.isFinite(Number(row.quote.etaMinutes)));
    if (withEta.length) {
      const fastest = [...withEta].sort(
        (a, b) => Number(a.quote.etaMinutes) - Number(b.quote.etaMinutes)
      )[0];
      badges[fastest.id].add("fastest");
    }
  }
  return badges;
}

function tierShares(quote) {
  const carrier = carrierFeeOf(quote);
  if (carrier == null) return null;
  const customerRaw = Number(quote.amount);
  const businessRaw = Number(quote.merchantSubsidy);
  const customer = Number.isFinite(customerRaw) ? roundMoney(customerRaw) : roundMoney(carrier);
  const business = Number.isFinite(businessRaw)
    ? roundMoney(businessRaw)
    : roundMoney(Math.max(0, carrier - customer));
  return { carrier: roundMoney(carrier), customer, business };
}

function sharesFor(quote, form) {
  const base = tierShares(quote);
  if (!base) return null;
  if (form.payer === "customer") return { ...base, customer: base.carrier, business: 0 };
  if (form.payer === "business") return { ...base, customer: 0, business: base.carrier };
  if (form.payer === "split" && form.splitEdited) {
    if (form.splitType === "fixed") {
      const fixed = Math.max(0, Number(form.splitVal) || 0);
      const customer = roundMoney(Math.min(fixed, base.carrier));
      return { ...base, customer, business: roundMoney(Math.max(0, base.carrier - customer)) };
    }
    const pct = Math.min(100, Math.max(0, Number(form.splitVal) || 0));
    const customer = roundMoney((base.carrier * pct) / 100);
    return { ...base, customer, business: roundMoney(Math.max(0, base.carrier - customer)) };
  }
  return base;
}

function splitOverridePayload(form) {
  if (form.payer === "customer") return { mode: "customer" };
  if (form.payer === "business") return { mode: "business" };
  if (form.payer === "split" && form.splitEdited) {
    return {
      mode: form.splitType === "fixed" ? "fixed" : "percent",
      value: Number(form.splitVal) || 0,
    };
  }
  return null;
}

function seedFromQuote(quote) {
  const tier = quote?.matchedTier;
  const shares = tierShares(quote);
  if (tier?.type === "percent_of_cost") {
    const businessPct = Number(tier.value) || 0;
    const customerPct = Math.max(0, Math.min(100, roundMoney(100 - businessPct)));
    return { splitType: "percent", splitVal: String(customerPct) };
  }
  return {
    splitType: "fixed",
    splitVal: shares ? String(shares.customer) : "0",
  };
}

function vatParts(total) {
  const gross = roundMoney(total);
  const excl = roundMoney(gross / 1.15);
  return { excl, vat: roundMoney(gross - excl), total: gross };
}

function paymentShareText(t, link) {
  return t("shipmentsPage.createManual.shareBody", { link });
}

function mailtoHref(subject, body) {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function whatsAppHref(phone, text) {
  const digits = String(phone || "").replace(/\D/g, "");
  const base = digits ? `https://wa.me/${digits}` : "https://wa.me/";
  return `${base}?text=${encodeURIComponent(text)}`;
}

function holdClock(ms) {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${pad(s % 60)}`;
}

function initialForm() {
  return {
    branchId: "",
    editPickup: false,
    pickupName: "",
    pickupPhone: "",
    pickupLat: "",
    pickupLng: "",
    pickupAddress: "",
    weightKg: "",
    packageType: "bag",
    shipmentValue: "",
    description: "",
    when: "now",
    schedDate: "",
    schedSlot: "",
    recipientName: "",
    recipientPhone: "",
    locationInput: "",
    plusCode: "",
    nationalShortCode: "",
    fullAddress: "",
    lat: "",
    lng: "",
    notes: "",
    carrierId: "",
    payer: "split",
    splitType: "fixed",
    splitVal: "",
    splitEdited: false,
    notifyWhatsapp: true,
  };
}

function pickupFromBranch(branch) {
  const p = branch?.pickup || {};
  return {
    pickupName: p.name || branch?.branchName || "",
    pickupPhone: p.phone || "",
    pickupLat: p.latitude != null && Number.isFinite(Number(p.latitude)) ? String(p.latitude) : "",
    pickupLng: p.longitude != null && Number.isFinite(Number(p.longitude)) ? String(p.longitude) : "",
    pickupAddress: p.address || "",
  };
}

function CreateManualShipmentPage() {
  const { t, i18n } = useTranslation("translation");
  const navigate = useNavigate();
  const isRtl = i18n.dir() === "rtl";
  const w = useCallback((key, opts) => t(`shipmentsPage.createManual.wizard.${key}`, opts), [t]);

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [branches, setBranches] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [loadingSetup, setLoadingSetup] = useState(true);
  const [paymentsEnabled, setPaymentsEnabled] = useState(null);
  const [highlight, setHighlight] = useState(false);
  const [quotes, setQuotes] = useState({});
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [errorBanner, setErrorBanner] = useState("");
  const [quoteRefresh, setQuoteRefresh] = useState(0);
  const [expiresAt, setExpiresAt] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const calendarRef = useRef(null);
  const quoteGen = useRef(0);
  const quoteErrorContextRef = useRef({ t, language: i18n.language });
  quoteErrorContextRef.current = { t, language: i18n.language };
  const refreshFor = useRef(0);

  const selectedBranch = useMemo(
    () => branches.find((b) => String(b.branchId) === String(form.branchId)),
    [branches, form.branchId]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingSetup(true);
      try {
        const enabled = [];
        let pageNum = 1;
        let hasNext = true;
        while (hasNext && pageNum <= 20) {
          const result = await fetchTrackHubBranches({ limit: 100, page: pageNum });
          enabled.push(...(result.data || []).filter(
            (row) => row.allowManualShipment === true && row.isEnabled === true
          ));
          hasNext = Boolean(result.pagination?.hasNextPage);
          pageNum += 1;
        }
        const carrierResult = await fetchTrackHubCarriers({ refresh: false });
        if (cancelled) return;
        setBranches(enabled);
        setCarriers((carrierResult.data || []).filter((c) => c.isActive !== false));
        setPaymentsEnabled(carrierResult?.manualShipmentPaymentsEnabled === true);
        if (enabled.length === 1) {
          setForm((prev) => ({
            ...prev,
            branchId: String(enabled[0].branchId),
            ...pickupFromBranch(enabled[0]),
          }));
        }
      } catch (error) {
        console.error("Failed to load create-shipment setup", error);
        if (!cancelled) {
          setBranches([]);
          setCarriers([]);
          setPaymentsEnabled(false);
        }
      } finally {
        if (!cancelled) setLoadingSetup(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (step !== 3) return undefined;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [step]);

  useEffect(() => {
    if (!calendarOpen) return undefined;
    const close = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setCalendarOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [calendarOpen]);

  const paymentsSkipped = paymentsEnabled === false;

  useEffect(() => {
    if (!paymentsSkipped) return;
    setForm((prev) => (
      prev.payer === "business" && prev.notifyWhatsapp === false
        ? prev
        : { ...prev, payer: "business", notifyWhatsapp: false }
    ));
  }, [paymentsSkipped]);

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrorBanner("");
  }, []);

  const selectBranch = useCallback((branchId) => {
    const branch = branches.find((b) => String(b.branchId) === String(branchId));
    setForm((prev) => ({
      ...prev,
      branchId: branchId || "",
      editPickup: false,
      ...(branch ? pickupFromBranch(branch) : {}),
    }));
    setQuotes({});
    setErrorBanner("");
  }, [branches]);

  const quoteKey = useMemo(
    () =>
      JSON.stringify({
        branchId: form.branchId,
        storeId: selectedBranch?.storeId || "",
        pickupName: form.pickupName,
        pickupPhone: form.pickupPhone,
        pickupLat: form.pickupLat,
        pickupLng: form.pickupLng,
        pickupAddress: form.pickupAddress,
        lat: form.lat,
        lng: form.lng,
        address: form.fullAddress,
        value: form.shipmentValue,
        phone: form.recipientPhone,
        name: form.recipientName,
        plusCode: form.plusCode,
        nationalShortCode: form.nationalShortCode,
        weightKg: form.weightKg,
        packageType: form.packageType,
        when: form.when,
        schedDate: form.schedDate,
        schedSlot: form.schedSlot,
      }),
    [
      form.branchId,
      form.pickupName,
      form.pickupPhone,
      form.pickupLat,
      form.pickupLng,
      form.pickupAddress,
      form.lat,
      form.lng,
      form.fullAddress,
      form.shipmentValue,
      form.recipientPhone,
      form.recipientName,
      form.plusCode,
      form.nationalShortCode,
      form.weightKg,
      form.packageType,
      form.when,
      form.schedDate,
      form.schedSlot,
      selectedBranch?.storeId,
    ]
  );

  useEffect(() => {
    setForm((prev) => (prev.carrierId ? { ...prev, carrierId: "" } : prev));
  }, [quoteKey]);

  useEffect(() => {
    if (step !== 3 || !carriers.length) return undefined;
    const gen = quoteGen.current + 1;
    quoteGen.current = gen;
    const snapshot = JSON.parse(quoteKey);
    const phoneE164 = formatKsaE164(normalizeKsaMobile(snapshot.phone) || "") || snapshot.phone;
    setQuotes(Object.fromEntries(carriers.map((c) => [String(c.carrierId || c.id), { status: "loading" }])));

    carriers.forEach((carrier) => {
      const id = String(carrier.carrierId || carrier.id || "");
      quoteTrackHubShipment({
        branchId: snapshot.branchId,
        storeId: snapshot.storeId || undefined,
        carrierId: id,
        customer_name: String(snapshot.name || "").trim(),
        customer_phone: phoneE164,
        shipmentValue: Number(snapshot.value) || 0,
        delivery_address: snapshot.address,
        nationalShortCode: snapshot.nationalShortCode || undefined,
        pickup: {
          lat: Number(snapshot.pickupLat),
          lng: Number(snapshot.pickupLng),
          address: snapshot.pickupAddress,
          contactName: snapshot.pickupName,
          contactPhone: snapshot.pickupPhone,
        },
        address_geo: {
          lat: Number(snapshot.lat),
          lng: Number(snapshot.lng),
          address: snapshot.address,
          plusCode: snapshot.plusCode || undefined,
          nationalShortCode: snapshot.nationalShortCode || undefined,
        },
        dropoff: {
          lat: Number(snapshot.lat),
          lng: Number(snapshot.lng),
          address: snapshot.address,
          contactName: String(snapshot.name || "").trim(),
          contactPhone: phoneE164,
          plusCode: snapshot.plusCode || undefined,
          nationalShortCode: snapshot.nationalShortCode || undefined,
        },
        package: (() => {
          const type = String(snapshot.packageType || "").trim();
          const rawWeight = String(snapshot.weightKg ?? "").trim();
          if (!rawWeight) return type ? { type } : null;
          const weight = Number(rawWeight);
          if (!Number.isFinite(weight) || weight <= 0 || weight > MAX_SHIPMENT_WEIGHT_KG) return null;
          return type ? { weightKg: weight, type } : { weightKg: weight };
        })(),
        packageSize: TRACKHUB_PACKAGE_SIZE[snapshot.packageType] || undefined,
        weightKg: (() => {
          const rawWeight = String(snapshot.weightKg ?? "").trim();
          if (!rawWeight) return undefined;
          const weight = Number(rawWeight);
          return Number.isFinite(weight) && weight > 0 ? weight : undefined;
        })(),
      })
        .then((result) => {
          if (quoteGen.current !== gen) return;
          if (!result?.success) {
            throw Object.assign(new Error(result?.message || "Quote failed"), { response: { data: result } });
          }
          setQuotes((prev) => ({
            ...prev,
            [id]: { status: "ok", quote: result.quote, distanceKm: result.distanceKm },
          }));
        })
        .catch((error) => {
          if (quoteGen.current !== gen) return;
          const { t: translate, language } = quoteErrorContextRef.current;
          const data = error?.response?.data;
          const restrictionMsg = quoteCarrierRestrictionMessage(data, translate, {
            maxWeightKg: MAX_SHIPMENT_WEIGHT_KG,
          });
          setQuotes((prev) => ({
            ...prev,
            [id]: {
              status: "error",
              error:
                restrictionMsg
                || formatTrackHubApiError(error, translate, { language })
                || translate("shipmentsPage.createManual.quoteError"),
            },
          }));
        });
    });
    return undefined;
  }, [step, quoteKey, quoteRefresh, carriers]);

  const quoteRows = useMemo(
    () =>
      carriers.map((carrier) => {
        const id = String(carrier.carrierId || carrier.id || "");
        return {
          id,
          name: carrier.name || id,
          ini: initials(carrier.name || id),
          ...(quotes[id] || { status: "loading" }),
        };
      }),
    [carriers, quotes]
  );

  const badges = useMemo(() => assignBadges(quoteRows), [quoteRows]);
  const selectedRow = quoteRows.find((row) => row.id === form.carrierId && row.status === "ok") || null;
  const selectedQuote = selectedRow?.quote || null;
  const shares = selectedQuote ? sharesFor(selectedQuote, form) : null;
  const quotesPending = step === 3 && quoteRows.some((row) => row.status === "loading");

  useEffect(() => {
    if (step !== 3 || quotesPending) {
      if (quotesPending) setExpiresAt(0);
      return;
    }
    if (quoteRows.some((row) => row.status === "ok")) {
      setExpiresAt(Date.now() + QUOTE_HOLD_MS);
    }
  }, [step, quotesPending, quoteKey, quoteRows]);

  useEffect(() => {
    if (!expiresAt || nowMs < expiresAt || step !== 3) return;
    if (refreshFor.current === expiresAt) return;
    refreshFor.current = expiresAt;
    setQuoteRefresh((n) => n + 1);
  }, [expiresAt, nowMs, step]);

  useEffect(() => {
    if (step !== 3 || form.carrierId || quotesPending) return;
    const recommended = quoteRows.find((row) => badges[row.id]?.has("recommended") && row.status === "ok");
    const first = quoteRows.find((row) => row.status === "ok");
    const pick = recommended || first;
    if (pick) setForm((prev) => (prev.carrierId ? prev : { ...prev, carrierId: pick.id }));
  }, [step, form.carrierId, quotesPending, quoteRows, badges]);

  useEffect(() => {
    if (!selectedQuote || form.splitEdited) return;
    const seed = seedFromQuote(selectedQuote);
    setForm((prev) => {
      if (prev.splitEdited || (prev.splitType === seed.splitType && prev.splitVal === seed.splitVal)) return prev;
      return { ...prev, ...seed };
    });
  }, [selectedQuote, form.splitEdited]);

  const km = roadKm(
    parseCoord(form.pickupLat),
    parseCoord(form.pickupLng),
    parseCoord(form.lat),
    parseCoord(form.lng)
  );

  const pickupMapTarget = useMemo(() => {
    const lat = parseCoord(form.pickupLat);
    const lng = parseCoord(form.pickupLng);
    if (lat == null || lng == null) return null;
    return { lat, lng, zoom: 15 };
  }, [form.pickupLat, form.pickupLng]);

  const handlePickupMapMove = useCallback((lat, lng) => {
    setForm((prev) => ({
      ...prev,
      pickupLat: String(lat),
      pickupLng: String(lng),
    }));
    setQuotes({});
    setErrorBanner("");
  }, []);

  const pickupDate = selectedPickupDate(form.schedDate);
  const pickupOffset = daysFromToday(pickupDate);
  const scheduleMaxYmd = ymd(dayDate(SCHEDULE_MAX_DAYS_AHEAD));
  const scheduleCalendarMinYmd = ymd(dayDate(QUICK_PICK_DAY_COUNT));

  const excludeCalendarDate = useCallback((date) => {
    const d = startOfDay(date);
    const min = dayDate(QUICK_PICK_DAY_COUNT);
    const max = dayDate(SCHEDULE_MAX_DAYS_AHEAD);
    if (d < min || d > max) return true;
    return !slotsForDate(d).some((item) => item.ok);
  }, []);

  const dayMeta = useCallback((date) => {
    const offset = daysFromToday(date);
    const lang = i18n.language || "en";
    return {
      title: offset === 0 ? w("today") : offset === 1 ? w("tomorrow") : new Intl.DateTimeFormat(lang, { weekday: "short" }).format(date),
      sub: new Intl.DateTimeFormat(lang, { day: "numeric", month: "short" }).format(date),
    };
  }, [i18n.language, w]);

  const whenLabel = form.when === "later" && form.schedSlot
    ? `${dayMeta(pickupDate).title} ${form.schedSlot}`
    : w("now");

  const fieldErrors = useCallback((which) => {
    const found = {};
    if (which === 1) {
      if (!form.branchId) found.branchId = w("chooseBranch");
      if (!form.pickupName.trim()) found.pickupName = w("missingPickupName");
      if (!String(form.pickupPhone).trim()) found.pickupPhone = w("missingPickupPhone");
      if (parseCoord(form.pickupLat) == null) found.pickupLat = w("missingPickupLat");
      if (parseCoord(form.pickupLng) == null) found.pickupLng = w("missingPickupLng");
      if (!(Number(form.shipmentValue) > 0)) found.shipmentValue = w("addValue");
      const rawWeight = String(form.weightKg ?? "").trim();
      if (rawWeight) {
        const weightNum = Number(form.weightKg);
        if (!Number.isFinite(weightNum) || weightNum <= 0) found.weightKg = w("missingMeasure");
        else if (weightNum > MAX_SHIPMENT_WEIGHT_KG) {
          found.weightKg = w("weightMaxExceeded", { max: MAX_SHIPMENT_WEIGHT_KG });
        }
      }
      if (form.when === "later") {
        const date = selectedPickupDate(form.schedDate);
        if (!form.schedSlot) found.schedSlot = w("pickSlot");
        else {
          const slot = slotsForDate(date).find((item) => item.s === form.schedSlot);
          if (!slot?.ok) found.schedSlot = w("slotPassed");
        }
      }
    } else if (which === 2) {
      if (!form.fullAddress.trim() || parseCoord(form.lat) == null || parseCoord(form.lng) == null) {
        found.fullAddress = w("resolveLocation");
      }
      if (parseCoord(form.lat) == null) found.lat = w("resolveLocation");
      if (parseCoord(form.lng) == null) found.lng = w("resolveLocation");
      if (!isValidKsaPhone(form.recipientPhone)) found.recipientPhone = w("addPhone");
      if (!form.recipientName.trim()) found.recipientName = w("addName");
    } else if (which === 3) {
      if (!carriers.length || (!quotesPending && !quoteRows.some((row) => row.status === "ok"))) {
        found.banner = w("noFleet");
      } else if (quotesPending) found.banner = w("gettingPrices");
      else if (expiresAt && Date.now() > expiresAt) found.banner = w("refreshingPrices");
      else if (!selectedQuote) found.banner = w("pickFleet");
    }
    return found;
  }, [form, w, quotesPending, quoteRows, expiresAt, selectedQuote, carriers.length]);

  const blockReason = useCallback((which) => {
    const found = fieldErrors(which);
    return Object.values(found)[0] || null;
  }, [fieldErrors]);

  const issues = highlight ? fieldErrors(step) : {};

  const revealPickup = (which) => {
    if (which !== 1) return;
    const found = fieldErrors(1);
    if (found.pickupName || found.pickupPhone || found.pickupLat || found.pickupLng) {
      setForm((prev) => (prev.editPickup ? prev : { ...prev, editPickup: true }));
    }
  };

  const goTo = (n) => {
    if (created) return;
    if (n < step) {
      setHighlight(false);
      setStep(n);
      return;
    }
    for (let i = 1; i < n; i += 1) {
      if (blockReason(i)) {
        revealPickup(i);
        setHighlight(true);
        setStep(i);
        return;
      }
    }
    setHighlight(false);
    setStep(n);
  };

  const goNext = () => {
    if (blockReason(step)) {
      revealPickup(step);
      setHighlight(true);
      return;
    }
    setHighlight(false);
    setStep((n) => Math.min(4, n + 1));
  };

  const handleDropoffChange = useCallback((patch, meta) => {
    setForm((prev) => {
      const next = { ...prev };
      if (Object.prototype.hasOwnProperty.call(patch, "query")) next.locationInput = patch.query;
      if (Object.prototype.hasOwnProperty.call(patch, "lat")) next.lat = patch.lat;
      if (Object.prototype.hasOwnProperty.call(patch, "lng")) next.lng = patch.lng;
      if (Object.prototype.hasOwnProperty.call(patch, "plusCode")) next.plusCode = patch.plusCode;
      if (Object.prototype.hasOwnProperty.call(patch, "nationalShortCode")) next.nationalShortCode = patch.nationalShortCode;
      if (Object.prototype.hasOwnProperty.call(patch, "address")) next.fullAddress = patch.address;
      if (meta?.source === "resolve" && patch.address && !String(prev.locationInput || "").trim()) {
        next.locationInput = patch.address;
      }
      return next;
    });
    setQuotes({});
  }, []);

  const editSplit = (patch) => {
    setForm((prev) => ({ ...prev, splitEdited: true, ...patch }));
  };

  const place = async () => {
    for (let i = 1; i <= 3; i += 1) {
      if (blockReason(i)) {
        revealPickup(i);
        setHighlight(true);
        setStep(i);
        return;
      }
    }
    const pkg = packagePayload(form);
    if (String(form.weightKg ?? "").trim() && !pkg) {
      setHighlight(true);
      setStep(1);
      return;
    }
    const phoneE164 = formatKsaE164(normalizeKsaMobile(form.recipientPhone));
    const scheduled = form.when === "later" ? slotDateOn(selectedPickupDate(form.schedDate), form.schedSlot) : null;
    setCreating(true);
    setErrorBanner("");
    const createPayload = {
      branchId: form.branchId,
      storeId: selectedBranch?.storeId,
      carrierId: form.carrierId,
      customer_name: form.recipientName.trim(),
      customer_phone: phoneE164,
      description: form.description.trim(),
      notes: form.notes.trim() || undefined,
      packageSize: TRACKHUB_PACKAGE_SIZE[form.packageType] || undefined,
      weightKg: pkg?.weightKg,
      shipmentValue: Number(form.shipmentValue) || 0,
      lang: String(i18n.language || "").toLowerCase().startsWith("ar") ? "ar" : "en",
      scheduledAt: scheduled ? scheduled.toISOString() : null,
      splitOverride: splitOverridePayload(paymentsSkipped ? { ...form, payer: "business" } : form),
      notifyWhatsapp: paymentsSkipped ? false : form.notifyWhatsapp === true,
      package: pkg,
      pickup: {
        lat: Number(form.pickupLat),
        lng: Number(form.pickupLng),
        address: form.pickupAddress,
        contactName: form.pickupName.trim(),
        contactPhone: form.pickupPhone,
      },
      delivery_address: form.fullAddress,
      nationalShortCode: form.nationalShortCode || undefined,
      address_geo: {
        lat: Number(form.lat),
        lng: Number(form.lng),
        address: form.fullAddress,
        plusCode: form.plusCode || undefined,
        nationalShortCode: form.nationalShortCode || undefined,
      },
      dropoff: {
        lat: Number(form.lat),
        lng: Number(form.lng),
        address: form.fullAddress,
        contactName: form.recipientName.trim(),
        contactPhone: phoneE164,
        notes: form.notes.trim(),
        plusCode: form.plusCode || undefined,
        nationalShortCode: form.nationalShortCode || undefined,
      },
    };
    try {
      let result;
      let lastError;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          result = await createManualTrackHubShipment(createPayload);
          if (!result?.success) {
            throw Object.assign(new Error(result?.message || "Create failed"), { response: { data: result } });
          }
          lastError = null;
          break;
        } catch (error) {
          lastError = error;
          if (attempt === 0 && isRetryableShipmentCreateError(error)) {
            await new Promise((resolve) => {
              window.setTimeout(resolve, 600);
            });
            continue;
          }
          throw error;
        }
      }
      if (lastError) throw lastError;
      setCreated(result);
    } catch (error) {
      const message = formatTrackHubApiError(error, t, { language: i18n.language })
        || t("shipmentsPage.createManual.createError");
      setErrorBanner(message);
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  const fieldClass = (key) => (issues[key] ? "cmsw__input cmsw__input--error" : "cmsw__input");
  const fieldClassLtr = (key) => `${fieldClass(key)} cmsw__input--ltr`;
  const fieldError = (key) => (issues[key] ? <p className="cmsw__err">{issues[key]}</p> : null);
  const reason = blockReason(step);
  const branchName = selectedBranch?.branchName || form.pickupName || "";
  const sizeLabel = w(`sizes.${form.packageType}`);
  const packageSummary = form.weightKg
    ? w("packageLine", { size: sizeLabel, weight: form.weightKg })
    : sizeLabel;
  const serviceSummary = w("serviceSummary", { service: w("serviceFood"), minutes: FOOD_SERVICE_SLA_MIN });
  const packageReviewSummary = w("packageReview", {
    size: sizeLabel,
    service: w("serviceFood"),
    minutes: FOOD_SERVICE_SLA_MIN,
  });
  const mappableBranches = useMemo(
    () => branches.filter((branch) => branchCoords(branch)),
    [branches]
  );
  const holdLeft = expiresAt ? expiresAt - nowMs : 0;
  const payerLabel = form.payer === "customer"
    ? w("payerCustomer")
    : form.payer === "business"
      ? w("payerBusiness")
      : w("payerSplit");

  const stepSub = (n) => {
    if (n === 1 && form.branchId) {
      return form.when === "later" && form.schedSlot ? `${branchName} · ${whenLabel}` : branchName;
    }
    if (n === 2) {
      if (parseCoord(form.lat) == null) return w("notSet");
      const who = form.recipientName.trim() || w("to");
      return km != null ? `${who} · ${km.toFixed(1)} km` : who;
    }
    if (n === 3) return w("notPicked");
    if (n === 4) return created ? w("steps.review") : w("notPlaced");
    return "";
  };

  const stepSubContent = (n) => {
    if (n === 3 && selectedRow && shares) {
      return (
        <>
          {selectedRow.name}
          {" · "}
          <MoneyInline value={shares.carrier} iconSize={11} />
        </>
      );
    }
    const text = stepSub(n);
    return text || null;
  };

  const timingText = () => {
    const mins = Number(selectedQuote?.etaMinutes);
    if (!Number.isFinite(mins)) return w("etaUnknown");
    const start = form.when === "later" && form.schedSlot
      ? slotDateOn(pickupDate, form.schedSlot)
      : new Date();
    const by = new Date(start.getTime() + mins * 60000);
    return w("timingLine", { minutes: Math.round(mins), by: `${pad(by.getHours())}:${pad(by.getMinutes())}` });
  };

  const deliveryLegMinutes = () => {
    const fromQuote = Number(selectedQuote?.deliveryEtaMinutes);
    if (Number.isFinite(fromQuote) && fromQuote > 0) return Math.round(fromQuote);
    const pickup = Number(selectedQuote?.etaMinutes);
    if (Number.isFinite(pickup) && pickup > 0) return Math.round(pickup * 1.2);
    return DEFAULT_DELIVERY_LEG_MIN;
  };

  const timingReviewText = () => {
    const pickupMin = Number(selectedQuote?.etaMinutes);
    if (!Number.isFinite(pickupMin)) return w("etaUnknown");
    const start = form.when === "later" && form.schedSlot
      ? slotDateOn(pickupDate, form.schedSlot)
      : new Date();
    const deliveredBy = new Date(start.getTime() + (pickupMin + deliveryLegMinutes()) * 60000);
    return w("timingReviewLine", {
      pickupMin: Math.round(pickupMin),
      deliveredBy: `${pad(deliveredBy.getHours())}:${pad(deliveredBy.getMinutes())}`,
    });
  };

  const spareDeliveryMinutes = () => {
    const pickupMin = Number(selectedQuote?.etaMinutes);
    if (!Number.isFinite(pickupMin)) return null;
    return Math.max(0, FOOD_SERVICE_SLA_MIN - Math.round(pickupMin + deliveryLegMinutes()));
  };

  const splitHint = () => {
    if (form.splitEdited) return w("splitCustomHint");
    const amount = Number(form.splitVal) || 0;
    const rule = form.splitType === "percent"
      ? w("splitPctRule", { amount: form.splitVal || 0 })
      : (
        <>
          {w("splitFlatRuleBefore")}
          <MoneyInline value={amount} />
          {w("splitFlatRuleAfter")}
        </>
      );
    return (
      <>
        {w("splitConfigHintBefore")}
        {rule}
        {w("splitConfigHintAfter")}
      </>
    );
  };

  const bill = shares ? vatParts(shares.carrier) : null;
  const continueLabel = step === 1
    ? w("continueCustomer")
    : step === 2
      ? w("continueCarrier")
      : w("continueReview");

  const mapExtra = km != null && branchName
    ? w("mapDragExtra", { km: km.toFixed(1), branch: branchName })
    : "";
  const spareMin = spareDeliveryMinutes();

  const renderPaymentNote = () => {
    if (!shares) return null;
    if (form.payer === "business") {
      return (
        <>
          {w("paymentNoteBusinessBefore")}
          <MoneyInline value={shares.carrier} iconSize={11} />
          {w("paymentNoteBusinessAfter", { branch: branchName })}
        </>
      );
    }
    if (form.payer === "customer") {
      return (
        <>
          {w("paymentNoteCustomerBefore")}
          <MoneyInline value={shares.customer} iconSize={11} />
          {w("paymentNoteCustomerAfter")}
        </>
      );
    }
    return (
      <>
        {w("paymentNoteSplitBefore")}
        <MoneyInline value={shares.customer} iconSize={11} />
        {w("paymentNoteSplitMiddle")}
        <MoneyInline value={shares.business} iconSize={11} />
        {w("paymentNoteSplitAfter", { branch: branchName })}
      </>
    );
  };

  const renderPaymentBill = (variant = "step3") => {
    if (!shares || !bill) return null;
    const paymentOnly = variant === "step4Rail";
    return (
      <div className="cmsw__bill">
        {paymentOnly ? (
          <div className="cmsw__bill-h cmsw__bill-h--carrier">
            <span>{selectedRow?.name}</span>
          </div>
        ) : null}
        {!paymentOnly ? (
          <div className="cmsw__bill-h">
            <b>{w("shipmentAmount")}</b>
            <span>{selectedRow?.name}</span>
          </div>
        ) : null}
        <div className="cmsw__bill-row">
          <span>{w("deliveryFee")}</span>
          <MoneyAmount value={bill.excl} />
        </div>
        <div className="cmsw__bill-row">
          <span>{w("vat15")}</span>
          <MoneyAmount value={bill.vat} />
        </div>
        <div className="cmsw__bill-row cmsw__bill-row--total">
          <span>{w("total")}</span>
          <MoneyAmount value={bill.total} iconSize={14} />
        </div>
        <div className="cmsw__bill-row">
          <span>{w("whoPays")}</span>
          <b>{payerLabel}</b>
        </div>
        <div className="cmsw__bar" aria-hidden="true">
          <i style={{ width: `${shares.carrier ? (shares.customer / shares.carrier) * 100 : 0}%` }} />
          <i style={{ width: `${shares.carrier ? (shares.business / shares.carrier) * 100 : 0}%` }} />
        </div>
        <div className="cmsw__legend-row">
          <i className="cmsw__sw cmsw__sw--brand" />
          <span>
            {form.payer === "split" && form.splitType === "fixed"
              ? (
                <>
                  {w("customerPaysFlatPrefix")}
                  <MoneyInline value={shares.customer} iconSize={11} />
                  {w("customerPaysFlatSuffix")}
                </>
              )
              : w("customerPaysLegend")}
          </span>
          <b className="cmsw__bill-amount" dir="ltr">
            <PriceDisplay value={shares.customer} iconSize={12} />
          </b>
        </div>
        <div className="cmsw__legend-row">
          <i className="cmsw__sw cmsw__sw--road" />
          <span>{form.payer === "split" ? w("businessPaysRest") : w("businessPays")}</span>
          <MoneyAmount value={shares.business} />
        </div>
        {paymentOnly ? (
          <p className="cmsw__payment-note">{renderPaymentNote()}</p>
        ) : null}
      </div>
    );
  };

  return (
    <div className="cmsw oh-page content-area" dir={isRtl ? "rtl" : "ltr"}>
      <div className="cmsw__crumb">
        <button type="button" onClick={() => navigate("/shipments")}>{w("backToList")}</button>
        {" / "}
        {w("title")}
      </div>
      <div className="cmsw__head"><h1>{w("title")}</h1></div>

      {loadingSetup ? <p className="cmsw__hint">{t("loading")}</p> : null}
      {!loadingSetup && branches.length === 0 ? (
        <section className="cmsw__panel"><p>{w("noBranches")}</p></section>
      ) : null}

      {!loadingSetup && branches.length > 0 && created ? (
        <section className="cmsw__panel cmsw__banner">
          <h2>
            {created.scheduled
              ? w("scheduledTitle")
              : created.coveredByBusiness
                ? t("shipmentsPage.createManual.coveredByBusinessTitle")
                : created.paymentLinkUrl
                  ? t("shipmentsPage.createManual.paymentPendingTitle")
                  : t("shipmentsPage.createManual.bookedTitle")}
          </h2>
          <p>
            {created.scheduled
              ? (created.coveredByBusiness || created.paymentLinkUrl
                ? w("scheduledBody")
                : w("scheduledWithoutPaymentBody"))
              : created.coveredByBusiness
                ? t("shipmentsPage.createManual.coveredByBusinessBody")
                : created.paymentLinkUrl
                  ? t("shipmentsPage.createManual.paymentPendingBody")
                  : t("shipmentsPage.createManual.bookedBody")}
          </p>
          {!created.coveredByBusiness && !created.paymentLinkUrl ? (
            <p>{t("shipmentsPage.paymentSkipped")}</p>
          ) : null}
          {(created.shipmentId || created.orderRef) ? (
            <dl className="cmsw__kv cmsw__banner-kv">
              {created.shipmentId ? (
                <>
                  <dt>{t("shipmentsPage.shipmentId")}</dt>
                  <dd className="cmsw__ltr-value">{created.shipmentId}</dd>
                </>
              ) : null}
              {created.orderRef ? (
                <>
                  <dt>{t("shipmentsPage.details.oneHubOrderId")}</dt>
                  <dd className="cmsw__ltr-value cmsw__order-ref">{created.orderRef}</dd>
                </>
              ) : null}
            </dl>
          ) : null}
          {created.paymentLinkUrl ? (
            <>
              <dl className="cmsw__kv cmsw__banner-kv">
                <dt>{t("shipmentsPage.paymentLink")}</dt>
                <dd>
                  <a className="cmsw__ref" href={created.paymentLinkUrl} target="_blank" rel="noopener noreferrer">{created.paymentLinkUrl}</a>
                </dd>
              </dl>
              <div className="cmsw__share">
                <button
                  type="button"
                  className="cmsw__share-btn"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(created.paymentLinkUrl);
                      setLinkCopied(true);
                      window.setTimeout(() => setLinkCopied(false), 1500);
                    } catch (error) {
                      console.error("Failed to copy payment link", error);
                    }
                  }}
                >
                  {linkCopied ? <Check size={16} strokeWidth={2} /> : <Copy size={16} strokeWidth={2} />}
                  {linkCopied
                    ? t("shipmentsPage.createManual.shareCopied")
                    : t("shipmentsPage.createManual.shareCopy")}
                </button>
                <a
                  className="cmsw__share-btn"
                  href={mailtoHref(
                    t("shipmentsPage.createManual.shareSubject"),
                    paymentShareText(t, created.paymentLinkUrl),
                  )}
                >
                  <Mail size={16} strokeWidth={2} />
                  {t("shipmentsPage.createManual.shareEmail")}
                </a>
                <a
                  className="cmsw__share-btn"
                  href={whatsAppHref(
                    formatKsaE164(normalizeKsaMobile(form.recipientPhone)),
                    paymentShareText(t, created.paymentLinkUrl),
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MessageCircle size={16} strokeWidth={2} />
                  {t("shipmentsPage.createManual.shareWhatsapp")}
                </a>
              </div>
            </>
          ) : null}
          <button type="button" className="cmsw__primary" onClick={() => navigate("/shipments")}>
            {t("shipmentsPage.createManual.done")}
          </button>
        </section>
      ) : null}

      {!loadingSetup && branches.length > 0 && !created ? (
        <>
          <nav className="cmsw__steps" aria-label={w("title")}>
            {["pickup", "customer", "carrier", "review"].map((key, index) => {
              const n = index + 1;
              const done = n < step;
              const current = n === step;
              const blocked = n > step && Boolean(blockReason(n - 1));
              return (
                <button
                  key={key}
                  type="button"
                  className={`cmsw__step${current ? " cmsw__step--cur" : ""}${done ? " cmsw__step--done" : ""}`}
                  onClick={() => goTo(n)}
                  disabled={current || blocked}
                  aria-current={current ? "step" : undefined}
                >
                  <span className="cmsw__dot">{done ? <Check size={14} strokeWidth={2.4} /> : n}</span>
                  <span className="cmsw__step-text">
                    <b>{w(`steps.${key}`)}</b>
                    <small>{done || current ? stepSubContent(n) : ""}</small>
                  </span>
                </button>
              );
            })}
          </nav>

          <div className="cmsw__grid">
            <div className="cmsw__col">
              {step === 1 ? (
                <>
                  <OrderSelectionField label={w("orderLabel")} />
                  <section className="cmsw__panel">
                    <div className="cmsw__ph">
                      <div>
                        <h2><Store size={18} /> {w("pickupTitle")}</h2>
                        <p className="cmsw__help">{w("pickupHelp")}</p>
                      </div>
                    </div>
                    <div className="cmsw__field">
                      <label htmlFor="cmsw-branch">{t("shipmentsPage.createManual.branch")}</label>
                      <select
                        id="cmsw-branch"
                        className={fieldClass("branchId")}
                        value={form.branchId}
                        onChange={(e) => selectBranch(e.target.value)}
                      >
                        <option value="">{t("shipmentsPage.createManual.selectBranch")}</option>
                        {branches.map((branch) => (
                          <option key={branch.branchId} value={String(branch.branchId)}>
                            {branch.branchName || branch.branchId}
                          </option>
                        ))}
                      </select>
                      {fieldError("branchId")}
                      {form.pickupAddress ? <span className="cmsw__hint">{form.pickupAddress}</span> : null}
                    </div>
                    {mappableBranches.length > 0 ? (
                      <div className="cmsw__pickup-map">
                        <PickupBranchMap
                          branches={branches}
                          selectedBranchId={form.branchId}
                          onSelectBranch={selectBranch}
                          editMode={form.editPickup && Boolean(form.branchId)}
                          pinTarget={pickupMapTarget}
                          onPinMove={handlePickupMapMove}
                        />
                        <p className="cmsw__hint">{w("pickupBranchMapHint")}</p>
                      </div>
                    ) : null}
                    <div className="cmsw__card cmsw__card--driver">
                      <span className="cmsw__lbl cmsw__driver-asks-title">{w("driverAsks")}</span>
                      {form.editPickup ? (
                        <div className="cmsw__row">
                          <div className="cmsw__field">
                            <label>{t("shipmentsPage.createManual.name")}</label>
                            <input className={fieldClass("pickupName")} value={form.pickupName} onChange={(e) => setField("pickupName", e.target.value)} />
                            {fieldError("pickupName")}
                          </div>
                          <div className="cmsw__field">
                            <label>{t("shipmentsPage.createManual.phone")}</label>
                            <input className={fieldClassLtr("pickupPhone")} value={form.pickupPhone} onChange={(e) => setField("pickupPhone", e.target.value)} />
                            {fieldError("pickupPhone")}
                          </div>
                          <div className="cmsw__field">
                            <label>{t("shipmentsPage.createManual.latitude")}</label>
                            <input className={fieldClassLtr("pickupLat")} value={form.pickupLat} onChange={(e) => setField("pickupLat", e.target.value)} />
                            {fieldError("pickupLat")}
                          </div>
                          <div className="cmsw__field">
                            <label>{t("shipmentsPage.createManual.longitude")}</label>
                            <input className={fieldClassLtr("pickupLng")} value={form.pickupLng} onChange={(e) => setField("pickupLng", e.target.value)} />
                            {fieldError("pickupLng")}
                          </div>
                          <div className="cmsw__field" style={{ gridColumn: "1 / -1" }}>
                            <label>{w("address")}</label>
                            <input className="cmsw__input" value={form.pickupAddress} onChange={(e) => setField("pickupAddress", e.target.value)} />
                          </div>
                        </div>
                      ) : (
                        <dl className="cmsw__kv cmsw__kv--driver">
                          <dt>{t("shipmentsPage.createManual.name")}</dt>
                          <dd>{form.pickupName || "—"}</dd>
                          <dt>{t("shipmentsPage.createManual.phone")}</dt>
                          <dd className="cmsw__ltr-value">{displayPhone(form.pickupPhone) || form.pickupPhone || "—"}</dd>
                          <dt>{w("address")}</dt>
                          <dd>{form.pickupAddress || "—"}</dd>
                          <dt>{w("coordinates")}</dt>
                          <dd className="cmsw__ltr-value cmsw__coords-value">
                            {form.pickupLat || "—"}, {form.pickupLng || "—"}
                          </dd>
                        </dl>
                      )}
                      <div className="cmsw__card-driver-foot">
                        <p className="cmsw__hint cmsw__driver-filled">
                          {branchName ? w("filledFrom", { branch: branchName }) : w("filledWhenEmpty")}
                        </p>
                        <button
                          type="button"
                          className={`cmsw__pickup-edit${form.editPickup ? " cmsw__pickup-edit--active" : ""}`}
                          onClick={() => setField("editPickup", !form.editPickup)}
                        >
                          {!form.editPickup ? <Pencil size={14} strokeWidth={2} /> : null}
                          {form.editPickup ? w("donePickup") : w("editPickup")}
                        </button>
                      </div>
                    </div>
                  </section>

                  <section className="cmsw__panel">
                    <div>
                      <h2><Package size={18} /> {w("packageTitle")}</h2>
                      <p className="cmsw__help">{w("packageHelp")}</p>
                    </div>
                    <div className="cmsw__field">
                      <span className="cmsw__lbl">{w("size")}</span>
                      <div className="cmsw__opts cmsw__opts--3">
                        {SIZES.map((size) => (
                          <button
                            key={size}
                            type="button"
                            className="cmsw__opt"
                            aria-pressed={form.packageType === size}
                            onClick={() => setField("packageType", size)}
                          >
                            <b>{w(`sizes.${size}`)}</b>
                            <span>{w(`sizes.${size}Hint`)}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="cmsw__row">
                      <div className="cmsw__field">
                        <label>
                          {w("weight")}
                          <span className="cmsw__label-note"> ({w("weightOptional")})</span>
                        </label>
                        <div className={`cmsw__affix cmsw__affix--ltr${issues.weightKg ? " cmsw__input--error" : ""}`}>
                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            value={form.weightKg}
                            max={MAX_SHIPMENT_WEIGHT_KG}
                            step="0.1"
                            min="0"
                            onChange={(e) => setField("weightKg", e.target.value)}
                          />
                          <span>kg</span>
                        </div>
                        {fieldError("weightKg")}
                      </div>
                      <div className="cmsw__field">
                        <label>{t("shipmentsPage.createManual.shipmentValue")}</label>
                        <div className={`cmsw__affix cmsw__affix--ltr${issues.shipmentValue ? " cmsw__input--error" : ""}`}>
                          <span className="cmsw__affix-sar" aria-hidden="true">
                            <SARIcon size={12} color="#6d6d6d" />
                          </span>
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={form.shipmentValue}
                            onChange={(e) => setField("shipmentValue", e.target.value)}
                          />
                        </div>
                        {fieldError("shipmentValue")}
                      </div>
                    </div>
                    <div className="cmsw__field">
                      <label>{t("shipmentsPage.createManual.description")}</label>
                      <textarea
                        className="cmsw__textarea"
                        rows={2}
                        placeholder={t("shipmentsPage.createManual.descriptionPlaceholder")}
                        value={form.description}
                        onChange={(e) => setField("description", e.target.value)}
                      />
                    </div>
                  </section>

                  <section className="cmsw__panel">
                    <div>
                      <h2><Clock size={18} /> {w("whenTitle")}</h2>
                      <p className="cmsw__help">{w("whenHelp")}</p>
                    </div>
                    <div className="cmsw__opts" role="group">
                      <button type="button" className="cmsw__opt" aria-pressed={form.when === "now"} onClick={() => setField("when", "now")}>
                        <b>{w("asSoon")}</b>
                        <span>{w("asSoonHint")}{branchName ? ` · ${branchName}` : ""}</span>
                      </button>
                      <button
                        type="button"
                        className="cmsw__opt"
                        aria-pressed={form.when === "later"}
                        onClick={() => setForm((prev) => ({
                          ...prev,
                          when: "later",
                          schedDate: prev.schedDate || ymd(dayDate(0)),
                        }))}
                      >
                        <b>{w("schedulePickup")}</b>
                        <span>{w("scheduleHint")}</span>
                      </button>
                    </div>
                    {form.when === "later" ? (
                      <div
                        className={`cmsw__when-schedule${calendarOpen ? " cmsw__when-schedule--cal-open" : ""}`}
                      >
                        <div className="cmsw__days" role="group">
                          {Array.from({ length: QUICK_PICK_DAY_COUNT }, (_, offset) => offset).map((offset) => {
                            const date = dayDate(offset);
                            const meta = dayMeta(date);
                            const any = slotsForDate(date).some((item) => item.ok);
                            return (
                              <button
                                key={offset}
                                type="button"
                                className="cmsw__day"
                                aria-pressed={ymd(date) === ymd(pickupDate)}
                                disabled={!any}
                                onClick={() => {
                                  setCalendarOpen(false);
                                  setForm((prev) => ({ ...prev, schedDate: ymd(date), schedSlot: "" }));
                                }}
                              >
                                <b>{meta.title}</b>
                                <span>{meta.sub}</span>
                              </button>
                            );
                          })}
                          <div className="cmsw__day-more-wrap" ref={calendarRef}>
                            <button
                              type="button"
                              className="cmsw__day cmsw__day--more"
                              aria-pressed={pickupOffset >= QUICK_PICK_DAY_COUNT || calendarOpen}
                              aria-expanded={calendarOpen}
                              onClick={() => setCalendarOpen((open) => !open)}
                            >
                              <Calendar size={16} strokeWidth={2} />
                              <b>{w("more")}</b>
                              <span>
                                {pickupOffset >= QUICK_PICK_DAY_COUNT ? dayMeta(pickupDate).sub : w("pickDate")}
                              </span>
                            </button>
                            {calendarOpen ? (
                              <div className="cmsw__cal-panel" role="dialog" aria-label={w("pickDate")}>
                                <DatesProvider
                                  settings={{
                                    locale: isRtl ? "ar" : "en",
                                    firstDayOfWeek: isRtl ? 6 : 0,
                                  }}
                                >
                                  <DatePicker
                                    className="cmsw__cal-picker"
                                    locale={isRtl ? "ar" : "en"}
                                    weekdayFormat={isRtl ? arabicWeekdayFormat : "dd"}
                                    size="sm"
                                    value={
                                      pickupOffset >= QUICK_PICK_DAY_COUNT ? ymd(pickupDate) : null
                                    }
                                    minDate={scheduleCalendarMinYmd}
                                    maxDate={scheduleMaxYmd}
                                    excludeDate={(date) => excludeCalendarDate(date)}
                                    onChange={(value) => {
                                      const next = toPickerYmd(value);
                                      if (!next) return;
                                      setForm((prev) => ({ ...prev, schedDate: next, schedSlot: "" }));
                                      setCalendarOpen(false);
                                    }}
                                  />
                                </DatesProvider>
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="cmsw__slotgrid">
                          {SLOT_GROUPS.map(([group, from, to]) => {
                            const slots = slotsForDate(pickupDate).filter((item) => item.t >= from && item.t < to);
                            if (!slots.length) return null;
                            return (
                              <div className="cmsw__slotgrp" key={group}>
                                <span className="cmsw__lbl">{w(group)}</span>
                                <div className="cmsw__slots">
                                  {slots.map((item) => (
                                    <button
                                      key={item.s}
                                      type="button"
                                      className="cmsw__slot"
                                      aria-pressed={form.schedSlot === item.s}
                                      disabled={!item.ok}
                                      onClick={() => setField("schedSlot", item.s)}
                                    >
                                      {item.s}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <p className="cmsw__hint">{w("slotFoot")}</p>
                        {fieldError("schedSlot")}
                      </div>
                    ) : null}
                  </section>
                </>
              ) : null}

              {step === 2 ? (
                <>
                  <section className="cmsw__panel">
                    <div>
                      <h2><User size={18} /> {w("customerTitle")}</h2>
                      <p className="cmsw__help">{w("customerHelp")}</p>
                    </div>
                    <div className="cmsw__row">
                      <div className="cmsw__field">
                        <label>{t("shipmentsPage.createManual.recipientPhone")}</label>
                        <div className={`cmsw__affix cmsw__affix--phone${issues.recipientPhone ? " cmsw__input--error" : ""}`}>
                          <span>+966</span>
                          <input
                            inputMode="numeric"
                            placeholder="5X XXX XXXX"
                            value={fmtPhone(phoneDigits(form.recipientPhone))}
                            onChange={(e) => setField("recipientPhone", phoneDigits(e.target.value))}
                          />
                        </div>
                        {fieldError("recipientPhone")}
                        <span className="cmsw__hint">{w("phoneHint")}</span>
                        <span className="cmsw__hint">{w("returningCustomerHint")}</span>
                      </div>
                      <div className="cmsw__field">
                        <label>{t("shipmentsPage.createManual.recipientName")}</label>
                        <input className={fieldClass("recipientName")} value={form.recipientName} onChange={(e) => setField("recipientName", e.target.value)} />
                        {fieldError("recipientName")}
                      </div>
                    </div>
                  </section>
                  <section className="cmsw__panel">
                    <div>
                      <h2><MapPin size={18} /> {w("dropoffTitle")}</h2>
                      <p className="cmsw__help">{w("dropoffHelp")}</p>
                    </div>
                    <DropoffLocationField
                      active
                      showHeading={false}
                      label={w("customerLocation")}
                      placeholder={w("dropoffHelp")}
                      hint=" "
                      mapHint={
                        parseCoord(form.lat) != null
                          ? w("mapDragHint", { extra: mapExtra })
                          : w("mapResolveHint")
                      }
                      chips={LOCATION_CHIPS.map((chip) => ({
                        ...chip,
                        label: w(chip.labelKey),
                      }))}
                      value={{
                        query: form.locationInput,
                        lat: form.lat,
                        lng: form.lng,
                        plusCode: form.plusCode,
                        nationalShortCode: form.nationalShortCode,
                        address: form.fullAddress,
                      }}
                      onChange={handleDropoffChange}
                    />
                    <div className="cmsw__field">
                      <label>{t("shipmentsPage.createManual.fullAddress")}</label>
                      <input className={fieldClass("fullAddress")} value={form.fullAddress} onChange={(e) => setField("fullAddress", e.target.value)} />
                      {fieldError("fullAddress")}
                    </div>
                    <div className="cmsw__row">
                      <div className="cmsw__field">
                        <label>{t("shipmentsPage.createManual.latitude")}</label>
                        <input className={fieldClassLtr("lat")} value={form.lat} onChange={(e) => setField("lat", e.target.value)} />
                        {fieldError("lat")}
                      </div>
                      <div className="cmsw__field">
                        <label>{t("shipmentsPage.createManual.longitude")}</label>
                        <input className={fieldClassLtr("lng")} value={form.lng} onChange={(e) => setField("lng", e.target.value)} />
                        {fieldError("lng")}
                      </div>
                    </div>
                    <div className="cmsw__field">
                      <label>{w("notes")}</label>
                      <input className="cmsw__input" placeholder={w("notesPlaceholder")} value={form.notes} onChange={(e) => setField("notes", e.target.value)} />
                    </div>
                  </section>
                </>
              ) : null}

              {step === 3 ? (
                <section className="cmsw__panel">
                  <div className="cmsw__ph">
                    <div>
                      <h2><Truck size={18} /> {w("fleetTitle")}</h2>
                      <p className="cmsw__help">{w("fleetHelp")}</p>
                    </div>
                    <button
                      type="button"
                      className={`cmsw__hold${holdLeft > 0 && holdLeft < 30000 ? " cmsw__hold--low" : ""}`}
                      onClick={() => setQuoteRefresh((n) => n + 1)}
                    >
                      <Clock size={14} />
                      {w("pricesHold", { time: holdLeft > 0 ? holdClock(holdLeft) : "0:00" })}
                    </button>
                  </div>
                  <div className="cmsw__quote-guide" role="note">
                    <AlertTriangle size={18} strokeWidth={2} aria-hidden="true" />
                    <div>
                      <b>{w("quoteGuideTitle")}</b>
                      <ul>
                        <li>{w("quoteGuideDistance", { km: QUOTE_GUIDE_MAX_KM })}</li>
                        <li>{w("quoteGuideWeight", { kg: QUOTE_GUIDE_MAX_KG })}</li>
                        <li>{w("quoteGuideVehicle")}</li>
                      </ul>
                    </div>
                  </div>
                  <div className="cmsw__list">
                    {quoteRows.map((row) => {
                      const pressed = form.carrierId === row.id && row.status === "ok";
                      const kinds = badges[row.id] || new Set();
                      const rowShares = row.status === "ok" ? sharesFor(row.quote, form) : null;
                      return (
                        <button
                          key={row.id}
                          type="button"
                          className="cmsw__q"
                          aria-pressed={pressed}
                          disabled={row.status !== "ok"}
                          onClick={() => setField("carrierId", row.id)}
                        >
                          {row.status === "loading" ? <span className="cmsw__spin" /> : <span className="cmsw__radio" />}
                          <span className="cmsw__who">
                            <span className="cmsw__av">{row.ini}</span>
                            <span>
                              <b>{row.name}</b>
                              <small>
                                {row.status === "loading"
                                  ? w("askingOne")
                                  : row.status === "error"
                                    ? row.error
                                    : Number.isFinite(Number(row.quote?.etaMinutes))
                                      ? w("eta", { minutes: row.quote.etaMinutes })
                                      : ""}
                              </small>
                            </span>
                          </span>
                          <span className="cmsw__price">
                            {row.status === "ok" ? (
                              <>
                                <MoneyAmount value={carrierFeeOf(row.quote)} iconSize={14} />
                                <small>{w("inclVat")}</small>
                              </>
                            ) : <span className="cmsw__sk" />}
                          </span>
                          {row.status === "ok" && rowShares ? (
                            <span className="cmsw__qtags">
                              {kinds.has("recommended") ? <span className="cmsw__tag cmsw__tag--rec">{w("recommended")}</span> : null}
                              {kinds.has("cheapest") ? <span className="cmsw__tag cmsw__tag--cheap">{w("cheapest")}</span> : null}
                              {kinds.has("fastest") ? <span className="cmsw__tag cmsw__tag--fast">{w("fastest")}</span> : null}
                              <span className="cmsw__tag cmsw__tag--share">
                                {w("shareTagCustomer")}
                                <MoneyInline value={rowShares.customer} iconSize={10} />
                                {" · "}
                                {w("shareTagBusiness")}
                                <MoneyInline value={rowShares.business} iconSize={10} />
                              </span>
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                  {fieldError("banner")}

                  <div className="cmsw__field">
                    <span className="cmsw__lbl">{w("whoPays")}</span>
                    <div className="cmsw__opts cmsw__opts--3">
                      <button
                        type="button"
                        className="cmsw__opt"
                        aria-pressed={!paymentsSkipped && form.payer === "customer"}
                        disabled={paymentsSkipped}
                        onClick={() => setField("payer", "customer")}
                      >
                        <span className="cmsw__opt-top">
                          <b>{w("payerCustomer")}</b>
                          {paymentsSkipped ? <span className="cmsw__tag cmsw__tag--soon">{w("comingSoon")}</span> : null}
                        </span>
                        <span className="cmsw__opt-sub">{w("payerCustomerHint")}</span>
                      </button>
                      <button
                        type="button"
                        className="cmsw__opt"
                        aria-pressed={form.payer === "business"}
                        onClick={() => setField("payer", "business")}
                      >
                        <span className="cmsw__opt-top">
                          <b>{w("payerBusiness")}</b>
                        </span>
                        <span className="cmsw__opt-sub">{w("payerBusinessHint")}</span>
                      </button>
                      <button
                        type="button"
                        className="cmsw__opt"
                        aria-pressed={!paymentsSkipped && form.payer === "split"}
                        disabled={paymentsSkipped}
                        onClick={() => setField("payer", "split")}
                      >
                        <span className="cmsw__opt-top">
                          <b>{w("payerSplit")}</b>
                          {paymentsSkipped ? <span className="cmsw__tag cmsw__tag--soon">{w("comingSoon")}</span> : null}
                        </span>
                        <span className="cmsw__opt-sub">
                          {form.splitType === "percent"
                            ? w("payerSplitHintPct", { amount: form.splitVal || 0 })
                            : (
                              <>
                                {w("payerSplitHintBefore")}
                                <MoneyInline value={Number(form.splitVal) || 0} iconSize={11} />
                                {w("payerSplitHintAfter")}
                              </>
                            )}
                        </span>
                      </button>
                    </div>
                  </div>

                  {form.payer === "split" ? (
                    <div className="cmsw__splitf">
                      <div className="cmsw__splitf-row">
                        <div className="cmsw__field">
                          <span className="cmsw__lbl">{w("splitBy")}</span>
                          <div className="cmsw__seg">
                            <button type="button" aria-pressed={form.splitType === "percent"} onClick={() => editSplit({ splitType: "percent" })}>
                              {w("percentage")}
                            </button>
                            <button type="button" aria-pressed={form.splitType === "fixed"} onClick={() => editSplit({ splitType: "fixed" })}>
                              {w("fixedAmount")}
                            </button>
                          </div>
                        </div>
                        <div className="cmsw__field">
                          <label>{w("customerPaysInput")}</label>
                          <div className="cmsw__affix cmsw__affix--ltr">
                            {form.splitType === "fixed" ? (
                              <span className="cmsw__affix-sar" aria-hidden="true">
                                <SARIcon size={12} color="#6d6d6d" />
                              </span>
                            ) : null}
                            <input
                              type="number"
                              min="0"
                              max={form.splitType === "percent" ? "100" : undefined}
                              value={form.splitVal}
                              onChange={(e) => editSplit({ splitVal: e.target.value })}
                            />
                            {form.splitType === "percent" ? <span>%</span> : null}
                            <button type="button" aria-label="-" onClick={() => editSplit({ splitVal: String(Math.max(0, (Number(form.splitVal) || 0) - 1)) })}>−</button>
                            <button type="button" aria-label="+" onClick={() => editSplit({ splitVal: String((Number(form.splitVal) || 0) + 1) })}>+</button>
                          </div>
                        </div>
                      </div>
                      <p className="cmsw__hint">
                        {splitHint()}
                        {form.splitEdited ? (
                          <>
                            {" "}
                            <button type="button" className="cmsw__linkish" onClick={() => setField("splitEdited", false)}>{w("useConfig")}</button>
                          </>
                        ) : null}
                      </p>
                    </div>
                  ) : null}

                  {shares && bill ? renderPaymentBill("step3") : null}
                </section>
              ) : null}

              {step === 4 && shares ? (
                <>
                  <section className="cmsw__panel">
                    <div>
                      <h2><ClipboardList size={18} /> {w("reviewTitle")}</h2>
                      <p className="cmsw__help">{w("reviewHelp")}</p>
                    </div>
                    <div className="cmsw__card">
                      <div className="cmsw__card-head">
                        <span className="cmsw__lbl">{w("pickupTitle")}</span>
                        <button type="button" className="cmsw__linkish" onClick={() => goTo(1)}>{w("edit")}</button>
                      </div>
                      <dl className="cmsw__kv">
                        <dt>{w("from")}</dt><dd>{branchName}{form.pickupAddress ? `, ${form.pickupAddress}` : ""}</dd>
                        <dt>{w("when")}</dt><dd>{form.when === "later" ? whenLabel : w("nowReady")}</dd>
                        <dt>{w("packageTitle")}</dt><dd>{packageReviewSummary}</dd>
                        <dt>{w("value")}</dt>
                        <dd><MoneyInline value={Number(form.shipmentValue)} iconSize={12} /></dd>
                        {form.description ? (<><dt>{t("shipmentsPage.createManual.description")}</dt><dd>{form.description}</dd></>) : null}
                      </dl>
                    </div>
                    <div className="cmsw__card">
                      <div className="cmsw__card-head">
                        <span className="cmsw__lbl">{w("customerTitle")}</span>
                        <button type="button" className="cmsw__linkish" onClick={() => goTo(2)}>{w("edit")}</button>
                      </div>
                      <dl className="cmsw__kv">
                        <dt>{w("to")}</dt>
                        <dd>
                          {form.recipientName}
                          {" · "}
                          <span className="cmsw__ltr-value">{displayPhone(form.recipientPhone)}</span>
                        </dd>
                        <dt>{w("address")}</dt><dd>{form.fullAddress}</dd>
                        <dt>{w("distance")}</dt><dd>{km != null ? w("kmByRoad", { km: km.toFixed(1) }) : "—"}</dd>
                      </dl>
                    </div>
                    <div className="cmsw__card">
                      <div className="cmsw__card-head">
                        <span className="cmsw__lbl">{w("fleetTitle")}</span>
                        <button type="button" className="cmsw__linkish" onClick={() => goTo(3)}>{w("change")}</button>
                      </div>
                      <dl className="cmsw__kv">
                        <dt>{w("steps.carrier")}</dt>
                        <dd>{selectedRow?.name}{w("loopsRatesSuffix")}</dd>
                        <dt>{w("timing")}</dt>
                        <dd className="cmsw__timing-row">
                          <span>{timingReviewText()}</span>
                          {spareMin != null && spareMin > 0 ? (
                            <span className="cmsw__tag cmsw__spare">
                              {w("spareMinutes", { minutes: spareMin })}
                            </span>
                          ) : null}
                        </dd>
                        <dt>{w("price")}</dt>
                        <dd>
                          <MoneyInline value={shares.carrier} iconSize={12} />
                          {" "}
                          {w("priceInclSuffix")}
                        </dd>
                      </dl>
                    </div>
                  </section>
                  <label className={`cmsw__toggle${paymentsSkipped ? " cmsw__toggle--soon" : ""}`}>
                    <input
                      type="checkbox"
                      checked={!paymentsSkipped && form.notifyWhatsapp}
                      disabled={paymentsSkipped}
                      onChange={(e) => setField("notifyWhatsapp", e.target.checked)}
                    />
                    <span className="cmsw__toggle-copy">
                      <span className="cmsw__toggle-title">
                        <b>{w("whatsapp", { name: form.recipientName || w("to") })}</b>
                        {paymentsSkipped ? <span className="cmsw__tag cmsw__tag--soon">{w("comingSoon")}</span> : null}
                      </span>
                      <span>{w("whatsappHint")}</span>
                    </span>
                  </label>
                  {errorBanner ? <p className="cmsw__err">{errorBanner}</p> : null}
                </>
              ) : null}
            </div>

            <aside className="cmsw__rail">
              <section className="cmsw__panel">
                {step === 4 && shares && bill ? (
                  <>
                    <div className="cmsw__card-head">
                      <b className="cmsw__rail-summary-title">{w("paymentDetails")}</b>
                      <button type="button" className="cmsw__linkish" onClick={() => goTo(3)}>{w("change")}</button>
                    </div>
                    {renderPaymentBill("step4Rail")}
                  </>
                ) : (
                  <>
                    <b className="cmsw__rail-summary-title">{w("summary")}</b>
                    {step < 2 ? <p className="cmsw__rail-summary-empty">{w("summaryEmpty")}</p> : (
                      <>
                        <div className="cmsw__rs">
                          <div className="cmsw__rs-h">
                            <i className="cmsw__d1" />
                            <span>{w("pickupFrom")}</span>
                            <button type="button" className="cmsw__linkish" onClick={() => goTo(1)}>{w("edit")}</button>
                          </div>
                          <dl className="cmsw__kv cmsw__rk">
                            <dt>{t("shipmentsPage.createManual.branch")}</dt><dd>{branchName || "—"}</dd>
                            <dt>{w("area")}</dt><dd>{form.pickupAddress || "—"}</dd>
                            <dt>{w("when")}</dt><dd>{whenLabel}</dd>
                            <dt>{w("packageTitle")}</dt><dd>{packageSummary}</dd>
                            <dt>{w("service")}</dt><dd>{serviceSummary}</dd>
                          </dl>
                        </div>
                        {step >= 3 ? (
                          <div className="cmsw__rs">
                            <div className="cmsw__rs-h">
                              <i className="cmsw__d2" />
                              <span>{w("shipTo")}</span>
                              <button type="button" className="cmsw__linkish" onClick={() => goTo(2)}>{w("edit")}</button>
                            </div>
                            <dl className="cmsw__kv cmsw__rk">
                              <dt>{w("to")}</dt><dd>{form.recipientName || "—"}</dd>
                              <dt>{t("shipmentsPage.createManual.phone")}</dt><dd className="cmsw__ltr-value">{displayPhone(form.recipientPhone) || "—"}</dd>
                              <dt>{w("address")}</dt><dd>{form.fullAddress || "—"}</dd>
                              <dt>{w("distance")}</dt><dd>{km != null ? w("kmByRoad", { km: km.toFixed(1) }) : "—"}</dd>
                            </dl>
                          </div>
                        ) : null}
                        {step >= 4 && selectedRow ? (
                          <div className="cmsw__rs">
                            <div className="cmsw__rs-h">
                              <i className="cmsw__d3" />
                              <span>{w("fleetTitle")}</span>
                              <button type="button" className="cmsw__linkish" onClick={() => goTo(3)}>{w("edit")}</button>
                            </div>
                            <dl className="cmsw__kv cmsw__rk">
                              <dt>{w("steps.carrier")}</dt><dd>{selectedRow.name}</dd>
                              <dt>{w("whenTitle")}</dt><dd>{timingText()}</dd>
                            </dl>
                          </div>
                        ) : null}
                      </>
                    )}
                  </>
                )}
              </section>
              <section className="cmsw__panel cmsw__actions">
                <button
                  type="button"
                  className="cmsw__primary"
                  disabled={creating}
                  onClick={step === 4 ? place : goNext}
                >
                  {creating
                    ? w("placing")
                    : step === 4
                      ? (
                        <>
                          {w("placeShipmentLabel")}
                          <MoneyInline value={shares?.carrier} iconSize={14} symbolColor="#ffffff" />
                        </>
                      )
                      : continueLabel}
                </button>
                {!reason ? (
                  <p className="cmsw__reason">
                    {step === 4 ? (form.when === "later" ? w("scheduledNote") : w("fleetBooked")) : w("ready")}
                  </p>
                ) : null}
                {step > 1 ? (
                  <button type="button" className="cmsw__secondary" onClick={() => goTo(step - 1)}>{w("back")}</button>
                ) : null}
              </section>
            </aside>
          </div>
        </>
      ) : null}
    </div>
  );
}

export default CreateManualShipmentPage;
